import { useState, useEffect } from "react";
import {
  Search,
  Filter,
  AlertTriangle,
  CheckCircle,
  AlertCircle,
  Info,
  Trash2,
  Flag,
  Loader,
  X,
  Activity,
  Clock,
  User,
  Zap,
  Calendar,
} from "lucide-react";
import { adminApi } from "../lib";
import { showSuccess, showError } from "../lib/toast";

interface ActivityLog {
  _id: string;
  user?: {
    _id: string;
    name: string;
    email: string;
  };
  userEmail?: string;
  userName?: string;
  userRole?: string;
  action: string;
  resourceType?: string;
  resourceId?: string;
  resourceName?: string;
  details: any;
  severity: "info" | "warning" | "error" | "critical";
  isSuspicious: boolean;
  userIp?: string;
  createdAt: string;
}

const AdminActivityLogsPage = () => {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [severityFilter, setSeverityFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedLog, setSelectedLog] = useState<ActivityLog | null>(null);
  const [summary, setSummary] = useState<any>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteDays, setDeleteDays] = useState(30);
  const [deleting, setDeleting] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 30,
    total: 0,
    pages: 0,
  });

  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await adminApi.getActivityLogs({
        page: pagination.page,
        limit: pagination.limit,
        severity: severityFilter || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        search: search || undefined,
      });
      if (res.success) {
        setLogs(res.data || []);
        setSummary(res.summary);
        setPagination((prev) => ({
          ...prev,
          total: res.pagination?.total || 0,
          pages: res.pagination?.pages || 0,
        }));
      }
    } catch (err: any) {
      console.error("Failed to fetch activity logs:", err);
      setError(err.message || "Failed to load activity logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [
    pagination.page,
    pagination.limit,
    severityFilter,
    startDate,
    endDate,
    search,
  ]);

  const handleDelete = async (olderThan?: number) => {
    const confirmMsg = olderThan
      ? `Delete all logs older than ${olderThan} days?`
      : "Delete selected logs?";
    if (!window.confirm(confirmMsg)) return;
    try {
      await adminApi.deleteActivityLogs(undefined, olderThan);
      showSuccess("Logs deleted successfully");
      fetchLogs();
    } catch (err) {
      showError("Failed to delete logs");
    }
  };

  const handleFlag = async (logId: string) => {
    const reason = prompt("Enter reason for flagging:");
    if (!reason) return;
    try {
      await adminApi.flagActivity(logId, reason);
      showSuccess("Activity flagged");
      fetchLogs();
    } catch (err) {
      showError("Failed to flag activity");
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case "warning":
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      case "error":
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case "critical":
        return <Zap className="w-4 h-4 text-red-600" />;
      default:
        return <Info className="w-4 h-4 text-blue-500" />;
    }
  };

  const formatAction = (action: string) => {
    return action.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-lg sm:text-2xl font-bold text-gray-900">
            Activity Logs
          </h1>
          <p className="text-xs text-gray-500 mt-0.5 sm:mt-1">
            Monitor system activities
          </p>
        </div>
        <button
          onClick={() => setShowDeleteModal(true)}
          className="px-2 sm:px-3 py-1.5 sm:py-2 text-xs text-red-600 border border-red-200 rounded-lg hover:bg-red-50 flex items-center gap-1"
        >
          <Trash2 className="w-3 h-3" />
          <span className="hidden sm:inline">Delete Old Logs</span>
          <span className="sm:hidden">Delete</span>
        </button>
      </div>

      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-4">
          {[
            {
              label: "Total",
              value: summary.total,
              icon: Activity,
              color: "text-gray-600",
            },
            {
              label: "Suspicious",
              value: summary.suspicious,
              icon: AlertTriangle,
              color: "text-yellow-600",
            },
            {
              label: "Warnings",
              value: summary.warning,
              icon: AlertCircle,
              color: "text-orange-600",
            },
            {
              label: "Errors",
              value: summary.error,
              icon: AlertCircle,
              color: "text-red-600",
            },
            {
              label: "Critical",
              value: summary.critical,
              icon: Zap,
              color: "text-red-700",
            },
          ].map((stat, i) => (
            <div
              key={i}
              className="bg-white rounded-lg border border-gray-200 p-2 sm:p-3"
            >
              <div className={`flex items-center gap-1 mb-0.5 ${stat.color}`}>
                <stat.icon className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="text-[10px] sm:text-xs font-medium">
                  {stat.label}
                </span>
              </div>
              <p className="text-lg sm:text-xl font-bold text-gray-900">
                {stat.value || 0}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by user, action, or resource..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-primary"
          />
        </div>

        <select
          value={severityFilter}
          onChange={(e) => {
            setSeverityFilter(e.target.value);
            setPagination((prev) => ({ ...prev, page: 1 }));
          }}
          className="px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-primary"
        >
          <option value="">All Severity</option>
          <option value="info">Info</option>
          <option value="warning">Warning</option>
          <option value="error">Error</option>
          <option value="critical">Critical</option>
        </select>

        <div className="flex items-center gap-2">
          <input
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-primary"
            placeholder="Start Date"
          />
          <span className="text-gray-400">to</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-primary"
            placeholder="End Date"
          />
        </div>

        {(startDate || endDate) && (
          <button
            onClick={() => {
              setStartDate("");
              setEndDate("");
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700"
          >
            Clear Dates
          </button>
        )}
      </div>

      {/* Activity Logs Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <Loader className="w-8 h-8 text-primary animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
            <p className="text-red-600 font-medium">Error loading logs</p>
            <p className="text-gray-500 text-sm mt-1">{error}</p>
            <button
              onClick={fetchLogs}
              className="mt-4 px-4 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary/90"
            >
              Retry
            </button>
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12">
            <Activity className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-700 font-medium">No activity logs yet</p>
            <p className="text-gray-400 text-xs mt-1">
              Activity logs will appear here as users interact with the platform
            </p>
            <button
              onClick={fetchLogs}
              className="mt-4 px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200"
            >
              Refresh
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left text-xs font-medium text-gray-500 px-2 sm:px-4 py-2 sm:py-3">
                    Time
                  </th>
                  <th className="text-left text-xs font-medium text-gray-500 px-2 sm:px-4 py-2 sm:py-3 hidden sm:table-cell">
                    User
                  </th>
                  <th className="text-left text-xs font-medium text-gray-500 px-2 sm:px-4 py-2 sm:py-3">
                    Action
                  </th>
                  <th className="text-left text-xs font-medium text-gray-500 px-2 sm:px-4 py-2 sm:py-3 hidden md:table-cell">
                    Severity
                  </th>
                  <th className="text-right text-xs font-medium text-gray-500 px-2 sm:px-4 py-2 sm:py-3">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {logs.map((log) => (
                  <tr key={log._id} className="hover:bg-gray-50">
                    <td className="px-2 sm:px-4 py-2 sm:py-3">
                      <p className="text-[10px] sm:text-xs text-gray-500">
                        {new Date(log.createdAt).toLocaleString()}
                      </p>
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 hidden sm:table-cell">
                      {log.user || log.userName ? (
                        <div>
                          <p className="text-xs font-medium text-gray-900 truncate max-w-[150px]">
                            {log.user?.name ||
                              log.userName ||
                              log.user?.email ||
                              log.userEmail ||
                              "Unknown"}
                          </p>
                          <p className="text-[10px] text-gray-500">
                            {log.user?.email || log.userEmail || ""}
                          </p>
                          {log.userRole && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600 capitalize">
                              {log.userRole}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">Guest</span>
                      )}
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3">
                      <p className="text-xs text-gray-700">
                        {formatAction(log.action)}
                      </p>
                      {log.resourceType && (
                        <p className="text-[10px] text-gray-400">
                          {log.resourceType}
                        </p>
                      )}
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 hidden md:table-cell">
                      <div className="flex items-center gap-1">
                        {getSeverityIcon(log.severity)}
                        <span className="text-[10px] sm:text-xs capitalize">
                          {log.severity}
                        </span>
                      </div>
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setSelectedLog(log)}
                          className="p-1 sm:p-1.5 text-gray-500 hover:text-primary hover:bg-primary/10 rounded-lg"
                          title="View Details"
                        >
                          <Info className="w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                        {!log.isSuspicious && log.severity !== "critical" && (
                          <button
                            onClick={() => handleFlag(log._id)}
                            className="p-1 sm:p-1.5 text-gray-500 hover:text-yellow-600 hover:bg-yellow-50 rounded-lg"
                            title="Flag Suspicious"
                          >
                            <Flag className="w-3 h-3 sm:w-4 sm:h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-gray-200">
            <div className="flex items-center gap-2">
              <p className="text-xs text-gray-500">
                Page {pagination.page} of {pagination.pages} ({pagination.total}{" "}
                total)
              </p>
              <select
                value={pagination.limit}
                onChange={(e) =>
                  setPagination((prev) => ({
                    ...prev,
                    limit: Number(e.target.value),
                    page: 1,
                  }))
                }
                className="px-2 py-1 text-xs border rounded-lg"
              >
                <option value={10}>10/page</option>
                <option value={30}>30/page</option>
                <option value={50}>50/page</option>
                <option value={100}>100/page</option>
              </select>
            </div>
            <div className="flex gap-1">
              <button
                onClick={() =>
                  setPagination((prev) => ({ ...prev, page: prev.page - 1 }))
                }
                disabled={pagination.page === 1}
                className="px-3 py-1 text-xs border rounded-lg disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() =>
                  setPagination((prev) => ({ ...prev, page: prev.page + 1 }))
                }
                disabled={pagination.page >= pagination.pages}
                className="px-3 py-1 text-xs border rounded-lg disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* View Log Modal */}
      {selectedLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setSelectedLog(null)}
          />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-gray-900">Activity Details</h3>
              <button
                onClick={() => setSelectedLog(null)}
                className="p-1 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto max-h-[60vh]">
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-gray-500">
                    Action
                  </label>
                  <p className="text-sm text-gray-900 font-medium">
                    {formatAction(selectedLog.action)}
                  </p>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500">
                    Severity
                  </label>
                  <div className="flex items-center gap-1 mt-1">
                    {getSeverityIcon(selectedLog.severity)}
                    <span className="text-sm capitalize">
                      {selectedLog.severity}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500">
                    User
                  </label>
                  <p className="text-sm text-gray-900">
                    {selectedLog.user?.name ||
                      selectedLog.userName ||
                      "Unknown"}
                  </p>
                  <p className="text-xs text-gray-500">
                    {selectedLog.user?.email || selectedLog.userEmail || ""}
                  </p>
                  {selectedLog.userRole && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600 capitalize mt-1">
                      {selectedLog.userRole}
                    </span>
                  )}
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500">
                    IP Address
                  </label>
                  <p className="text-sm text-gray-900">
                    {selectedLog.userIp || "N/A"}
                  </p>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500">
                    Time
                  </label>
                  <p className="text-sm text-gray-900">
                    {new Date(selectedLog.createdAt).toLocaleString()}
                  </p>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500">
                    Details
                  </label>
                  <pre className="mt-1 p-3 bg-gray-100 rounded-lg text-xs text-gray-700 overflow-x-auto whitespace-pre-wrap">
                    {JSON.stringify(selectedLog.details, null, 2)}
                  </pre>
                </div>

                {selectedLog.isSuspicious && (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-xs font-medium text-yellow-700">
                      Flagged as suspicious
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowDeleteModal(false)}
          />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900 text-lg">
                Delete Activity Logs
              </h3>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="p-1 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Choose how many days of logs to keep. Logs older than the selected
              days will be permanently deleted.
            </p>

            <div className="space-y-3">
              <label className="text-sm font-medium text-gray-700">
                Delete logs older than:
              </label>
              <select
                value={deleteDays}
                onChange={(e) => setDeleteDays(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-primary"
              >
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
                <option value={60}>60 days</option>
                <option value={90}>90 days</option>
              </select>

              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    setDeleting(true);
                    try {
                      await adminApi.deleteActivityLogs(undefined, deleteDays);
                      showSuccess("Logs deleted successfully");
                      setShowDeleteModal(false);
                      fetchLogs();
                    } catch (err) {
                      showError("Failed to delete logs");
                    } finally {
                      setDeleting(false);
                    }
                  }}
                  disabled={deleting}
                  className="flex-1 px-3 py-2 text-sm text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {deleting ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      <span>Delete</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminActivityLogsPage;
